/**
 * @fileoverview Types for HubSpot integration
 *
 * Including:
 * - Types corresponding to rjsf schemas for action step inputs and outputs
 * - Types for HubSpot API responses
 * - Types for request data sent to HubSpot API
 * - RJSF schema that are shared across r/w action steps and webhooks
 *
 * Organized by entities:
 * - Companies
 * - Deals
 * - Properties

 */

import * as yup from 'yup'

// GENERIC ASSOCIATION RESULT SCHEMA --------------------------------------------------------------

const hubSpotAssociationResultSchema = yup.object({
  results: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        type: yup.string().required(),
      }),
    )
    .optional()
    .nullable(),
})

// COMPANY -----------------------------------------------------------------------------------------

/**
 * A subset of default company properties defined by HubSpot
 * https://knowledge.hubspot.com/properties/hubspot-crm-default-company-properties
 */
export const HUBSPOT_DEFAULT_COMPANY_PROPERTIES = [
  'address',
  'address2',
  'city',
  'closedate',
  'country',
  'name',
  'phone',
  'state',
  'type',
  'zip',
]

/**
 * Filter operators supported by HubSpot's CRM search endpoints.
 *
 * Operator choice depends on the property's type, and getting it wrong returns an empty result
 * set rather than an error:
 *  - enumeration properties (dropdown, multiple checkboxes) match reliably with IN / NOT_IN
 *  - CONTAINS_TOKEN / NOT_CONTAINS_TOKEN are for string properties and support `*` wildcards
 *  - HAS_PROPERTY / NOT_HAS_PROPERTY take no value
 *
 * @see https://developers.hubspot.com/docs/api-reference/latest/crm/search-the-crm
 */
export const HUBSPOT_SEARCH_FILTER_OPERATORS = [
  'EQ',
  'NEQ',
  'LT',
  'LTE',
  'GT',
  'GTE',
  'IN',
  'NOT_IN',
  'HAS_PROPERTY',
  'NOT_HAS_PROPERTY',
  'CONTAINS_TOKEN',
  'NOT_CONTAINS_TOKEN',
] as const

export type HubSpotSearchFilterOperator = (typeof HUBSPOT_SEARCH_FILTER_OPERATORS)[number]

/** Operators that take a list of values rather than a single one. */
export const HUBSPOT_SEARCH_LIST_OPERATORS: HubSpotSearchFilterOperator[] = ['IN', 'NOT_IN']

/** Operators that assert only on presence, so any supplied value is ignored. */
export const HUBSPOT_SEARCH_UNARY_OPERATORS: HubSpotSearchFilterOperator[] = [
  'HAS_PROPERTY',
  'NOT_HAS_PROPERTY',
]

/** One row of the action step's filter table. */
export const hubSpotSearchFilterSchema = yup.object({
  propertyName: yup.string().required(),
  operator: yup.string().oneOf(HUBSPOT_SEARCH_FILTER_OPERATORS).required(),
  value: yup.string().optional().nullable(),
})

export type HubSpotSearchFilter = yup.InferType<typeof hubSpotSearchFilterSchema>

/** Action step input schema for HubSpot - Get Company / Companies */
export const hubSpotGetCompaniesActionStepInputSchema = yup.object({
  companyId: yup.string().optional().nullable(),
  filters: yup.array().of(hubSpotSearchFilterSchema).optional().nullable(),
})

/** HubSpot Company Object Schema */
export const hubSpotCompanyObjectSchema = yup.object({
  id: yup.string().required(),
  properties: yup.object({
    address: yup.string().optional().nullable(),
    address2: yup.string().optional().nullable(),
    city: yup.string().optional().nullable(),
    closedate: yup.string().datetime().optional().nullable(),
    country: yup.string().optional().nullable(),
    createdate: yup.string().datetime().optional().nullable(),
    hs_lastmodifieddate: yup.string().datetime().optional().nullable(),
    hs_object_id: yup.string().optional().nullable(),
    name: yup.string().optional().nullable(),
    phone: yup.string().optional().nullable(),
    state: yup.string().optional().nullable(),
    type: yup.string().optional().nullable(),
    zip: yup.string().optional().nullable(),
  }),
  createdAt: yup.string().datetime().optional().nullable(),
  updatedAt: yup.string().datetime().optional().nullable(),
  archived: yup.boolean().optional().nullable(),
})
export type HubSpotCompanyObject = yup.InferType<typeof hubSpotCompanyObjectSchema>

/** HubSpot Company Association Types */
export const HUBSPOT_COMPANY_ASSOCIATION_TYPES = ['company', 'deal']

export const hubSpotCompanyObjectWithAssociationsResponseSchema = hubSpotCompanyObjectSchema.concat(
  yup.object({
    associations: yup
      .object({
        companies: hubSpotAssociationResultSchema,
        deals: hubSpotAssociationResultSchema,
      })
      .optional()
      .nullable(),
  }),
)
export type HubSpotCompanyObjectWithAssociations = yup.InferType<
  typeof hubSpotCompanyObjectWithAssociationsResponseSchema
>

/** HubSpot Get List of Companies Response Schema */
export const hubSpotGetCompaniesResponseSchema = yup.object({
  results: yup.array().of(hubSpotCompanyObjectSchema).required(),
  paging: yup
    .object({
      next: yup
        .object({
          after: yup.string(),
          link: yup.string(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
})

/**
 * Search responses carry a `total` and cap out at 10,000 records; paging past that returns a 400.
 * @see https://developers.hubspot.com/docs/api-reference/latest/crm/search-the-crm
 */
export const hubSpotSearchCompaniesResponseSchema = yup.object({
  total: yup.number().optional().nullable(),
  results: yup.array().of(hubSpotCompanyObjectSchema).required(),
  paging: yup
    .object({
      next: yup
        .object({
          after: yup.string(),
          link: yup.string(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
})

// LINE ITEM -----------------------------------------------------------------------------------------

/**
 * A subset of default line item properties defined by HubSpot
 */
export const HUBSPOT_DEFAULT_LINE_ITEM_PROPERTIES = [
  'name',
  'description',
  'hs_sku',
  'quantity',
  'price',
  'amount',
  'discount',
  'hs_discount_percentage',
  'tax',
]

/** HubSpot Line Item Object Schema */
export const hubSpotLineItemObjectSchema = yup.object({
  id: yup.string().required(),
  properties: yup.object({
    name: yup.string().optional().nullable(),
    description: yup.string().optional().nullable(),
    hs_sku: yup.string().optional().nullable(),
    quantity: yup.number().optional().nullable(),
    price: yup.number().optional().nullable(),
    amount: yup.number().optional().nullable(),
    discount: yup.number().optional().nullable(),
    hs_discount_percentage: yup.number().optional().nullable(),
    tax: yup.number().optional().nullable(),
  }),
  createdAt: yup.string().datetime().optional().nullable(),
  updatedAt: yup.string().datetime().optional().nullable(),
  archived: yup.boolean().optional().nullable(),
})
export type HubSpotLineItemObject = yup.InferType<typeof hubSpotLineItemObjectSchema>

// DEALS -----------------------------------------------------------------------------------------

/**
 * A subset of default deal properties defined by HubSpot
 *
 * https://knowledge.hubspot.com/properties/hubspots-default-deal-properties
 */
export const HUBSPOT_DEFAULT_DEAL_PROPERTIES = [
  'amount',
  'amount_in_home_currency',
  'closedate',
  'createdate',
  'days_to_close',
  'deal_currency_code',
  'dealname',
  'dealstage',
  'dealtype',
  'hs_acv',
  'hs_closed_amount',
  'hs_closed_amount_in_home_currency',
  'hs_closed_won_date',
  'hs_is_closed',
  'hs_is_closed_won',
  'hs_lastmodifieddate',
  'hs_mrr',
  'hs_object_id',
  'hubspot_owner_assigneddate',
  'hubspot_owner_id',
  'num_associated_contacts',
  'pipeline',
]

/** Action step input schema for HubSpot - Get Deal(s) */
export const hubSpotGetDealsActionStepInputSchema = yup.object({
  dealId: yup.string().optional().nullable(),
})

/** Base schema for HubSpot create/update deals action step input */
export const hubSpotWriteDealActionStepInputBaseSchema = yup.object({
  defaultDealProperties: yup
    .object({
      amount: yup.number().optional().nullable(),
      closedate: yup.string().optional().nullable(),
      dealname: yup.string().optional().nullable(),
      dealstage: yup.string().optional().nullable(),
      dealtype: yup.string().optional().nullable(),
      hubspot_owner_id: yup.string().optional().nullable(),
      pipeline: yup.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  customDealProperties: yup
    .array()
    .of(
      yup.object({
        key: yup.string().required(),
        value: yup.string().nullable(),
      }),
    )
    .optional()
    .nullable(),
})
export type HubSpotWriteDealActionStepInputBase = yup.InferType<
  typeof hubSpotWriteDealActionStepInputBaseSchema
>

/** Action step input schema for HubSpot Create Deal */
export const hubSpotCreateDealActionStepInputSchema = hubSpotWriteDealActionStepInputBaseSchema

/** Action step input schema for HubSpot Update Deal */
export const hubSpotUpdateDealActionStepInputSchema = yup
  .object({
    hubSpotDealId: yup.string().required(),
  })
  .concat(hubSpotWriteDealActionStepInputBaseSchema)

/**
 * HubSpot Create/Update Deal Request Type
 *
 * Note: Cannot define a yup schema here because there are dynamic keys
 */
export type HubSpotCreateUpdateDealRequest = {
  properties: {
    amount?: number
    closedate?: string
    dealname?: string
    dealstage?: string
    dealtype?: string
    hubspot_owner_id?: string
    pipeline?: string
  } & {
    [key: string]: string | number | boolean
  }
}

/** HubSpot Deal Object Schema */
export const hubSpotDealObjectSchema = yup.object({
  id: yup.string().required(),
  properties: yup.object({
    amount: yup.string().optional().nullable(),
    closedate: yup.string().datetime().optional().nullable(),
    createdate: yup.string().datetime().optional().nullable(),
    days_to_close: yup.string().optional().nullable(),
    dealname: yup.string().optional().nullable(),
    dealstage: yup.string().optional().nullable(),
    dealtype: yup.string().optional().nullable(),
    hs_acv: yup.string().optional().nullable(),
    hs_closed_amount: yup.string().optional().nullable(),
    hs_is_closed: yup.boolean().optional().nullable(),
    hs_is_closed_won: yup.boolean().optional().nullable(),
    hs_mrr: yup.string().optional().nullable(),
    hubspot_owner_id: yup.string().optional().nullable(),
    pipeline: yup.string().optional().nullable(),
  }),
  createdAt: yup.string().datetime().optional().nullable(),
  updatedAt: yup.string().datetime().optional().nullable(),
  archived: yup.boolean().optional().nullable(),
})
export type HubSpotDealObject = yup.InferType<typeof hubSpotDealObjectSchema>

/** HubSpot Deal Association Types */
export const HUBSPOT_DEAL_ASSOCIATION_TYPES = ['company', 'line_item']

export const hubSpotDealObjectWithAssociationsResponseSchema = hubSpotDealObjectSchema.concat(
  yup.object({
    associations: yup
      .object({
        companies: hubSpotAssociationResultSchema,
        'line items': hubSpotAssociationResultSchema,
      })
      .optional()
      .nullable(),
  }),
)
export type HubSpotDealObjectWithAssociations = yup.InferType<
  typeof hubSpotDealObjectWithAssociationsResponseSchema
>
/** HubSpot Get List of Deals Response Schema */
export const hubSpotGetDealsResponseSchema = yup.object({
  results: yup.array().of(hubSpotDealObjectWithAssociationsResponseSchema).required(),
  paging: yup
    .object({
      next: yup
        .object({
          after: yup.string(),
          link: yup.string(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
})

/** HubSpot Deal with Detailed Associated Objects */
export const hubSpotDealWithAssociatedObjectsSchema = hubSpotDealObjectSchema.concat(
  yup.object({
    associations: yup
      .object({
        companies: yup.array().of(hubSpotCompanyObjectSchema).optional().nullable(),
        lineItems: yup.array().of(hubSpotLineItemObjectSchema).optional().nullable(),
      })
      .optional()
      .nullable(),
  }),
)

export type HubSpotDealWithAssociatedObjects = yup.InferType<
  typeof hubSpotDealWithAssociatedObjectsSchema
>

// PROPERTIES -----------------------------------------------------------------------------------------

/** HubSpot Custom Property Schema */
export const hubSpotCustomPropertySchema = yup.object({
  updatedAt: yup.string().datetime().optional().nullable(),
  createdAt: yup.string().datetime().optional().nullable(),
  name: yup.string().required(),
  label: yup.string().required(),
  type: yup.string().optional().nullable(),
  fieldType: yup.string().optional().nullable(),
  createdUserId: yup.string().optional().nullable(),
  archived: yup.boolean().optional().nullable(),
})

/** HubSpot Get List of Custom Properties Response Schema */
export const hubSpotGetCustomPropertiesResponseSchema = yup.object({
  results: yup.array().of(hubSpotCustomPropertySchema).required(),
})

// COMPANY ASSOCIATIONS -----------------------------------------------------------------------------------------

/** HubSpot Company with Detailed Associated Objects */
export const hubSpotCompanyWithAssociatedObjectsSchema = hubSpotCompanyObjectSchema.concat(
  yup.object({
    associations: yup
      .object({
        companies: yup.array().of(hubSpotCompanyObjectSchema).optional().nullable(),
        deals: yup.array().of(hubSpotDealObjectSchema).optional().nullable(),
      })
      .optional()
      .nullable(),
  }),
)

export type HubSpotCompanyWithAssociatedObjects = yup.InferType<
  typeof hubSpotCompanyWithAssociatedObjectsSchema
>

/** HubSpot Get List of Company IDs Response Schema */
export const hubSpotGetCompanyIdsResponseSchema = yup.object({
  results: yup
    .array()
    .of(
      yup
        .object({
          id: yup.string().optional().nullable(),
        })
        .optional()
        .nullable(),
    )
    .required(),
  paging: yup
    .object({
      next: yup
        .object({
          after: yup.string(),
          link: yup.string(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
})
